import {
  AlertTriangle,
  CircleDotDashed,
  Eye,
  LockKeyhole,
  LockKeyholeOpen,
  RotateCcw,
} from "lucide-react";
import {
  Button,
  Badge,
  DataTable,
  EmployeeIdentity,
  Tooltip,
} from "../components/ui/index.js";
import { monthLabel } from "../components/payroll/PeriodPickers.js";
import {
  payslipStatusLabel,
  payslipStatusTone,
} from "../components/payroll/PayslipPresentational.js";
import { payslipRequiresReplacement } from "./payrollInputIntegrity.js";
import type { ReactNode } from "react";
import type {
  AdminUser,
  TimesheetVersionState,
  PayslipRecord,
  BalanceResult,
} from "../types/payrollAdmin.js";
export interface PrepareEmployeeRow {
  user: AdminUser;
  timesheet: TimesheetVersionState;
  timesheetLoading: boolean;
  timesheetError: boolean;
  pohodaEmployee: { ready: boolean; statusLabel?: string } | undefined;
  activePayslip: PayslipRecord | null;
  calculation: BalanceResult | null;
  requiredAction: string;
}
export interface PrepareOverviewModel {
  month: string;
  monthClosed: boolean;
  sourceEnabled: boolean;
  hasPayrollAccess: boolean;
  pendingEmployeeId: string | number | null;
  uploadPending: boolean;
  rows: PrepareEmployeeRow[];
  totalCount: number;
}
export interface PrepareOverviewActions {
  onToggleTimesheet: (employeeId: string | number) => void;
  onReplacePayslip: (name: string) => void;
  onOpenPayslip: (record: Pick<PayslipRecord, "id">) => void;
}
export function PayrollPrepareOverview({
  model,
  actions,
  header,
}: {
  model: PrepareOverviewModel;
  actions: PrepareOverviewActions;
  header: ReactNode;
}) {
  const {
    month,
    monthClosed,
    sourceEnabled,
    hasPayrollAccess,
    pendingEmployeeId,
    uploadPending,
    rows: filteredEmployeeOverviewRows,
    totalCount,
  } = model;
  const {
    onToggleTimesheet: requestTimesheetToggle,
    onReplacePayslip: requestPayslipReplacement,
    onOpenPayslip: openPayslipSheet,
  } = actions;
  return (
    <section className="min-w-0 lg:flex lg:h-full lg:min-h-0 lg:flex-col">
      {header}
      <div className="min-h-0 lg:flex-1">
        <DataTable className="hidden overflow-hidden rounded-xl border border-border bg-background md:block">
          <DataTable.ScrollContainer className="overflow-x-auto">
            <DataTable.Content
              aria-label={`${monthLabel(month)} Work profiles`}
              className="w-full table-fixed text-sm tabular-nums"
            >
              <DataTable.Header className="bg-background text-xs text-foreground-muted">
                <DataTable.Column
                  isRowHeader
                  className="w-[25%] px-4 py-2.5 text-left font-medium"
                >
                  Employee
                </DataTable.Column>
                <DataTable.Column className="w-[14%] px-3 py-2.5 text-left font-medium">
                  Pohoda input
                </DataTable.Column>
                <DataTable.Column className="w-[20%] px-3 py-2.5 text-left font-medium">
                  Payslip
                </DataTable.Column>
                <DataTable.Column className="w-[14%] px-3 py-2.5 text-left font-medium">
                  Calculation
                </DataTable.Column>
                <DataTable.Column className="w-[17%] px-3 py-2.5 text-left font-medium">
                  Required action
                </DataTable.Column>
                <DataTable.Column className="w-[10%] px-4 py-2.5 text-right font-medium">
                  Lock
                </DataTable.Column>
              </DataTable.Header>
              <DataTable.Body>
                {filteredEmployeeOverviewRows.map((row) => {
                  const name = row.user.displayName || row.user.username;
                  const payslipTone = row.activePayslip
                    ? payslipStatusTone(row.activePayslip.matchStatus)
                    : "neutral";
                  const lockPending =
                    pendingEmployeeId !== null &&
                    pendingEmployeeId === row.user.id;
                  return (
                    <DataTable.Row
                      id={row.user.id}
                      key={row.user.id}
                      className="border-t border-border text-foreground transition-colors duration-200 hover:bg-elevated/50"
                    >
                      <DataTable.Cell className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <EmployeeIdentity
                            name={name}
                            subtitle={row.user.payrollEmployeeCode || undefined}
                          />
                        </div>
                      </DataTable.Cell>
                      <DataTable.Cell className="px-3 py-2">
                        <Badge
                          variant={
                            row.pohodaEmployee?.ready ? "success" : "warning"
                          }
                          className="border border-current/30"
                        >
                          {row.pohodaEmployee?.ready
                            ? "Ready"
                            : row.pohodaEmployee?.statusLabel || "Waiting"}
                        </Badge>
                      </DataTable.Cell>
                      <DataTable.Cell className="px-3 py-2">
                        {row.activePayslip ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge
                              variant={
                                payslipTone === "neutral"
                                  ? "default"
                                  : payslipTone
                              }
                              className="border border-current/30"
                            >
                              {payslipStatusLabel(
                                row.activePayslip.matchStatus,
                              )}
                            </Badge>
                          </div>
                        ) : (
                          <span className="text-xs text-foreground-muted">
                            Not uploaded
                          </span>
                        )}
                      </DataTable.Cell>
                      <DataTable.Cell className="px-3 py-2">
                        {row.calculation ? (
                          <Badge
                            variant={
                              row.calculation.approved
                                ? "success"
                                : row.calculation.statusColor === "red"
                                  ? "danger"
                                  : "warning"
                            }
                            className="border border-current/30"
                          >
                            {row.calculation.approved
                              ? "Approved"
                              : row.calculation.statusColor === "red"
                                ? "Blocked"
                                : "Review"}
                          </Badge>
                        ) : (
                          <span className="text-xs text-foreground-muted">
                            Not calculated
                          </span>
                        )}
                      </DataTable.Cell>
                      <DataTable.Cell className="px-3 py-2">
                        <span
                          className={`font-medium ${row.requiredAction === "Complete" ? "text-success" : "text-foreground"}`}
                        >
                          {row.requiredAction}
                        </span>
                      </DataTable.Cell>
                      <DataTable.Cell className="px-4 py-2 text-right">
                        <Tooltip
                          content={
                            row.timesheetLoading
                              ? "Checking live lock state."
                              : row.timesheetError
                                ? "Lock state could not be loaded."
                                : row.timesheet.locked
                                  ? `Locked for ${monthLabel(month)}. Activate to unlock.`
                                  : row.timesheet.available === false
                                    ? "Link this employee to OpenProject before locking."
                                    : `Ready to lock for ${monthLabel(month)}.`
                          }
                        >
                          <Button
                            variant={row.timesheet.locked ? "ghost" : "warning"}
                            size="sm"
                            type="button"
                            onClick={() => requestTimesheetToggle(row.user.id)}
                            disabled={
                              monthClosed ||
                              (!row.timesheet.locked && !sourceEnabled) ||
                              lockPending ||
                              row.timesheetLoading ||
                              row.timesheetError ||
                              (!row.timesheet.locked &&
                                row.timesheet.available === false) ||
                              !hasPayrollAccess
                            }
                            aria-label={
                              row.timesheetLoading
                                ? `Checking ${name}'s timesheet lock state`
                                : row.timesheetError
                                  ? `${name}'s timesheet lock state is unavailable`
                                  : `${row.timesheet.locked ? "Unlock" : "Lock"} ${name}'s timesheet for ${monthLabel(month)}`
                            }
                            className={`flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50 ${row.timesheet.locked ? "border-success/40 bg-success-subtle text-success hover:bg-success/20" : "border-warning/40 bg-warning-subtle text-warning hover:bg-warning/20"}`}
                          >
                            {lockPending || row.timesheetLoading ? (
                              <CircleDotDashed
                                size={16}
                                className="animate-spin"
                                aria-hidden="true"
                              />
                            ) : row.timesheetError ? (
                              <AlertTriangle size={16} aria-hidden="true" />
                            ) : row.timesheet.locked ? (
                              <LockKeyhole size={16} aria-hidden="true" />
                            ) : (
                              <LockKeyholeOpen size={16} aria-hidden="true" />
                            )}
                          </Button>
                        </Tooltip>
                      </DataTable.Cell>
                    </DataTable.Row>
                  );
                })}
              </DataTable.Body>
            </DataTable.Content>
          </DataTable.ScrollContainer>
        </DataTable>
        <div className="grid min-w-0 gap-3 p-4 md:hidden">
          {filteredEmployeeOverviewRows.map((row) => {
            const name = row.user.displayName || row.user.username;
            const lockPending =
              pendingEmployeeId !== null && pendingEmployeeId === row.user.id;
            return (
              <article
                key={row.user.id}
                className="min-w-0 max-w-full rounded-2xl border border-border bg-surface p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="break-words font-semibold text-foreground">
                      {name}
                    </h3>
                    <p className="mt-1 truncate text-xs text-foreground-muted">
                      {row.user.payrollEmployeeCode || "Profile code missing"}
                    </p>
                  </div>
                  <Tooltip
                    content={
                      row.timesheetLoading
                        ? "Checking live lock state."
                        : row.timesheetError
                          ? "Lock state could not be loaded."
                          : row.timesheet.locked
                            ? `Locked for ${monthLabel(month)}. Activate to unlock.`
                            : row.timesheet.available === false
                              ? "Link this employee to OpenProject before locking."
                              : `Ready to lock for ${monthLabel(month)}.`
                    }
                  >
                    <Button
                      variant={row.timesheet.locked ? "ghost" : "warning"}
                      size="sm"
                      type="button"
                      onClick={() => requestTimesheetToggle(row.user.id)}
                      disabled={
                        monthClosed ||
                        (!row.timesheet.locked && !sourceEnabled) ||
                        lockPending ||
                        row.timesheetLoading ||
                        row.timesheetError ||
                        (!row.timesheet.locked &&
                          row.timesheet.available === false) ||
                        !hasPayrollAccess
                      }
                      aria-label={`${row.timesheet.locked ? "Unlock" : "Lock"} ${name}'s timesheet`}
                      className={`flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50 ${row.timesheet.locked ? "border-success/40 bg-success-subtle text-success" : "border-warning/40 bg-warning-subtle text-warning"}`}
                    >
                      {lockPending || row.timesheetLoading ? (
                        <CircleDotDashed
                          size={17}
                          className="animate-spin"
                          aria-hidden="true"
                        />
                      ) : row.timesheetError ? (
                        <AlertTriangle size={17} aria-hidden="true" />
                      ) : row.timesheet.locked ? (
                        <LockKeyhole size={17} aria-hidden="true" />
                      ) : (
                        <LockKeyholeOpen size={17} aria-hidden="true" />
                      )}
                    </Button>
                  </Tooltip>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <dt className="text-foreground-muted">Pohoda</dt>
                    <dd className="mt-1 font-medium text-foreground">
                      {row.pohodaEmployee?.ready
                        ? "Ready"
                        : row.pohodaEmployee?.statusLabel || "Waiting"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-foreground-muted">Payslip</dt>
                    <dd className="mt-1 font-medium text-foreground">
                      {row.activePayslip
                        ? payslipStatusLabel(row.activePayslip.matchStatus)
                        : "Not uploaded"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-foreground-muted">Calculation</dt>
                    <dd className="mt-1 font-medium text-foreground">
                      {row.calculation
                        ? row.calculation.approved
                          ? "Approved"
                          : row.calculation.statusColor === "red"
                            ? "Blocked"
                            : "Review"
                        : "Not calculated"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-foreground-muted">Next action</dt>
                    <dd
                      className={`mt-1 font-medium ${row.requiredAction === "Complete" ? "text-success" : "text-foreground"}`}
                    >
                      {row.requiredAction}
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
                  {row.activePayslip &&
                    (payslipRequiresReplacement(row.activePayslip) ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => requestPayslipReplacement(name)}
                        disabled={uploadPending}
                        className="inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-danger/30 px-3 text-xs font-medium text-danger"
                      >
                        <RotateCcw size={15} aria-hidden="true" />
                        Replace PDF
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => openPayslipSheet(row.activePayslip!)}
                        className="inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-border px-3 text-xs font-medium text-foreground-muted"
                      >
                        <Eye size={15} aria-hidden="true" />
                        Open payslip &amp; values
                      </Button>
                    ))}
                </div>
              </article>
            );
          })}
        </div>
        {totalCount === 0 && (
          <p className="px-5 py-6 text-center text-sm text-foreground-muted">
            No employees are enabled for payroll in {monthLabel(month)}.
          </p>
        )}
        {totalCount > 0 && filteredEmployeeOverviewRows.length === 0 && (
          <p className="px-5 py-6 text-center text-sm text-foreground-muted">
            No employees match this search and status filter.
          </p>
        )}
      </div>
    </section>
  );
}
